import React, { createContext, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import SandBox from './SandBox';
import { SandBoxTx, Transaction } from '../../types/rest/assetCatalogTypes';
import { Tab, Tabs } from 'react-bootstrap';
import SandBoxTransaction from './SandBoxTransaction/SandBoxTransaction';
import acApi from '../../axios/ac/acApi';
import { useIsMount } from '../../hooks/useIsMountHook';
import _ from 'lodash';
import styles from '../ui/icons/DraftIcon.module.css';
import clsx from 'clsx';
import { SandboxApiResponse } from './SandBox.types';
import { useDispatch } from 'react-redux';
import { showToastNotification } from '../../store/ui/toasts/toastsActions';
import { ToastType } from '../../store/ui/toasts/toastsTypes';
import FullPageLoader from '../loaders/FullPageLoader';
import SandboxDataPreviewModal from './SandboxDataPreviewModal';
import SandboxProcessingActions from './SandboxProcessingActions/SandboxProcessingActions';

interface SandboxProps {
  onClose: () => void;
  transaction: Transaction;
}

// Define the type for the context state
interface SandBoxContextState {
  state: SandBoxContext;
  setState: React.Dispatch<React.SetStateAction<SandBoxContext>>;
}

export const SandBoxContext = createContext<SandBoxContextState | null>(null);

export interface SandBoxContext {
  baseTransaction: Transaction;
  sandboxTransaction: SandBoxTx | null;
  response: SandboxApiResponse | null;
  isSandboxDirty?: boolean;
  isAutofilling?: boolean;
}

const getInitialSandbox = async (transaction: Transaction): Promise<SandBoxTx | null> => {
  try {
    await acApi.sandbox.getSandbox(); // timing buffer — lets any in-flight PATCH land
    const sandbox = (await acApi.sandbox.getSandbox(transaction.id))?.data?.result;
    if (sandbox) {
      return { ...sandbox, id: sandbox.transactionId };
    }
    return null;
  } catch (error) {
    console.error('Error fetching sandbox:', error);
    return null;
  }
};

const SandboxModal = ({ transaction, onClose }: SandboxProps) => {
  const [state, setState] = useState<SandBoxContext>({
    baseTransaction: transaction,
    sandboxTransaction: null,
    response: null,
    isSandboxDirty: false,
    isAutofilling: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDataPreviewOpen, setIsDataPreviewOpen] = useState<boolean>(false);
  const dispatch = useDispatch();

  const fetchTransaction = async () => {
    try {
      const sandboxTx: SandBoxTx = await getInitialSandbox(transaction);
      const definitionDirty = !_.isEqual(state.baseTransaction.dsl, sandboxTx?.dsl);

      setState(prevState => ({
        ...prevState,
        sandboxTransaction: sandboxTx,
        isSandboxDirty: definitionDirty,
      }));
    } catch (error) {
      dispatch(
        showToastNotification(ToastType.Danger, 'Error loading sandbox', 'Failed to load the sandbox transaction.'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isMount = useIsMount();
  useEffect(() => {
    if (isMount) {
      acApi.sandbox.resetCache().catch((_err: unknown) => undefined);
      setIsLoading(true);
      fetchTransaction();
    }
  }, []);

  const draftIcon = state.isSandboxDirty ? (
    <span title="Changes Detected" className={clsx('ml-3', styles.draft)}></span>
  ) : (
    <></>
  );

  const tabStyle = { minHeight: 'calc(100vh - 234px)' };

  return (
    <SandBoxContext.Provider value={{ state, setState }}>
      <Modal
        show={true}
        enforceFocus={false}
        id="qa-modal-bulk-tag-modal"
        centered={true}
        backdrop={true}
        dialogClassName="modal-full-screen"
        onHide={handleClose}
      >
        <Modal.Header closeButton id="qa-bulk-tagger-modal-btn-close-icon" className={'py-4'}>
          <h5 className="modal-title" id="exampleModalLabel">
            Transaction Testing
          </h5>
        </Modal.Header>
        <Modal.Body>
          {isDataPreviewOpen && transaction.serviceId && (
            <SandboxDataPreviewModal serviceId={transaction.serviceId} onClose={() => setIsDataPreviewOpen(false)} />
          )}
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ position: 'absolute', top: '0.25rem', right: '0', zIndex: 1, color: '#5405ff' }}
              onClick={() => setIsDataPreviewOpen(true)}
            >
              Service Data
            </button>
            <Tabs className="mb-0">
              <Tab title={'Transaction Sandbox'} eventKey="sandbox">
                <div className="bg-light" style={tabStyle}>
                  {isLoading ? (
                    <FullPageLoader className={'pt-10'} loadingText={'Loading Sandbox...'} />
                  ) : (
                    state.sandboxTransaction && <SandBox />
                  )}
                </div>
              </Tab>
              <Tab title={<span>Transaction Definition {draftIcon}</span>} eventKey="transaction" unmountOnExit={true}>
                <div className="bg-light p-4" style={tabStyle}>
                  {state.sandboxTransaction && <SandBoxTransaction />}
                </div>
              </Tab>
              <Tab title={'Processing Actions'} eventKey="processingActions" unmountOnExit={true}>
                <div className="bg-light" style={tabStyle}>
                  {state.sandboxTransaction && (
                    <SandboxProcessingActions transactionId={state.sandboxTransaction.transactionId} />
                  )}
                </div>
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleClose} id="qa-modal-bulk-tag-modal-btn-close">
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </SandBoxContext.Provider>
  );
};

export default SandboxModal;
